<template>
  <a-spin :spinning="!rights">
    <div class="w-full h-full flex gap-x-[10px]">
      <div v-if="inline" class="rights-badge">
        {{ rightsNames()[rightsType] }}
      </div>
      <RightsDesc
        :desc="rightsCompare"
        :rights-type="rightsType"
        :onCloseParent="onCloseParent"
        :class="[descClass, { '!pt-[50px]': inline }]"
      />

      <div
        v-if="isGlobalVersion()"
        class="flex-1 flex justify-between rounded-[12px] bg-back-1"
        :class="contentClass"
      >
        <div class="flex-1 flex pt-[32px] pl-[31px] flex-col">
          <PayStripeHPP
            v-model:agreement-checked="agreementChecked"
            :order="rawOrder"
            :loading="creating"
            :price-text="currentFees.price"
            :buy-type="currentFees.buy_type"
          />
        </div>
        <div class="w-[1px] bg-[#f1f1f1]"></div>
        <div class="flex-1 p-[30px]">
          <TitleLabel :title="$t('gou-mai-xin-xi')" class="pb-[16px]" />
          <a-alert v-if="!inline" class="mb-[20px]" type="info" show-icon>
            <template #message>
              {{ $t('sheng-ji-qi-ta-zhang-hao') }}
              <a @click="switchAccount">{{ $t('qie-huan-zhang-hao') }}</a>
              {{ $t('huo') }}
              <a @click="toCreateTeam">
                {{ $t('xin-jian-tuan-dui-sheng-ji') }}
              </a>
            </template>
          </a-alert>
          <div class="buyInfo">
            <div class="buyInfo__label">{{ $t('dang-qian-tuan-dui') }}</div>
            <div class="buyInfo__text">
              {{ userInfo?.organization_name + '(' + userInfo?.account_name + ')' }}
            </div>
          </div>
          <div class="buyInfo">
            <div class="buyInfo__label">{{ $t('shi-chang') }}</div>
            <div class="buyInfo__text">
              {{ $t('currentfeesdays-tian', [currentFees.days]) }}
            </div>
          </div>
          <div class="buyInfo">
            <div class="buyInfo__label">{{ $t('you-xiao-qi') }}</div>
            <div class="buyInfo__text">
              {{ $t('dao-qi-shi-jian') }}
              {{ expiredDateRange }}
            </div>
          </div>
          <div v-if="inline" class="h-[1px] bg-[#f1f1f1]"></div>
          <a-button
            v-if="inline"
            type="primary"
            block
            ghost
            class="h-[40px] mt-[20px]"
            @click="showCurrentContact"
          >
            {{ $t('zi-xun-gu-wen') }}
          </a-button>
        </div>
      </div>
      <div
        v-else
        class="flex-1 flex justify-between rounded-[12px] bg-back-1"
        :class="contentClass"
      >
        <PaySucc
          v-if="paySucc"
          :title="paySuccTitle"
          :desc="paySuccDesc"
          :invite-props="{
            isInviteUser,
            award: inviterAward,
            cb: goInvite,
          }"
          @on-success="onCloseParent"
        >
        </PaySucc>
        <div v-else class="flex-1 p-[30px]">
          <TitleLabel :title="$t('gou-mai-xin-xi')" class="pb-[16px]" />
          <a-alert v-if="!inline" class="mb-[20px]" type="info" show-icon>
            <template #message>
              {{ $t('sheng-ji-qi-ta-zhang-hao') }}
              <a @click="switchAccount">{{ $t('qie-huan-zhang-hao') }}</a>
              {{ $t('huo') }}
              <a @click="toCreateTeam">
                {{ $t('xin-jian-tuan-dui-sheng-ji') }}
              </a>
            </template>
          </a-alert>
          <div class="buyInfo">
            <div class="buyInfo__label">{{ $t('dang-qian-tuan-dui') }}</div>
            <div class="buyInfo__text">
              {{ userInfo?.organization_name + '(' + userInfo?.account_name + ')' }}
            </div>
          </div>
          <div class="buyInfo">
            <div class="buyInfo__label">{{ $t('shi-chang') }}</div>
            <div class="buyInfo__text">
              {{ $t('currentfeesdays-tian', [currentFees.days]) }}
            </div>
          </div>
          <div class="buyInfo">
            <div class="buyInfo__label">{{ $t('you-xiao-qi') }}</div>
            <div class="buyInfo__text">
              {{ $t('dao-qi-shi-jian') }}
              {{ expiredDateRange }}
            </div>
          </div>
          <div v-if="inline" class="h-[1px] bg-[#f1f1f1]"></div>
          <a-button
            v-if="inline"
            type="primary"
            block
            ghost
            class="h-[40px] mt-[20px]"
            @click="showCurrentContact"
          >
            {{ $t('zi-xun-gu-wen') }}
          </a-button>
        </div>
        <div class="w-[1px] bg-[#f1f1f1]"></div>
        <div v-if="order.status === undefined" class="flex flex-col w-[270px]">
          <div v-if="isRenew" class="rights-tip">
            <ExclamationCircleFilled class="text-[#FFAC15] text-[16px] mt-[6px] mr-[8px]" />
            <span>
              {{
                $t(
                  'tao-can-sheng-xiao-zhong-xu-yue-hou-quan-yi-jiang-yu-rightsviewgetexpiredata-sheng-xiao',
                  [expiredDateRange],
                )
              }}
            </span>
          </div>
          <PayQrcode
            v-model:pay-type="payType"
            v-model:agreement-checked="agreementChecked"
            :loading="creating"
            :qrcode="qrcode"
            :behavior="behavior"
            :reorder="createOrder"
            :price-text="currentFees.price"
          >
            <Consultant class="mt-[20px]" :rightType="rightsType" @transfer="onTransferClick" />
          </PayQrcode>
        </div>
        <PaySuccTip v-else-if="paySucc" class="max-w-[252px]" :url="getQrcodeURI('b', '4')" />
        <PayFail v-else @back="onRePay" />
      </div>
    </div>
  </a-spin>
</template>

<script setup lang="ts">
/**
 * 团队入门版购买、续费
 */
import { BUY_TYPE, ORDER_BEHAVIOR, ORDER_TYPE } from '~/api/interface';
import { USER_LEVEL_KEY, type UserInfo } from '~/api/interface/auth';
import type { IPostCreateOrderConfig } from '~/api/interface/order';
import { PageKey } from '~/constants/page';
import { PRODUCT_IMAGES } from '~/constants/product';
import { showContactNew } from '~/hooks/useModal/modals/contact';
import { useTime } from '~/hooks/useTime';
import { isGlobalVersion } from '~/utils/area.ts';
import { rightsNames } from '~/utils/constant';
import { getQrcodeURI } from '~/utils/url';
import useCheckUserInvited from '../../../useCheckUserInvited';
import useOrder, { useRights } from '../../../useOrder';
import Consultant from '../../consultant.vue';
import PayFail from '../../pay-fail.vue';
import PayQrcode from '../../pay-qrcode.vue';
import PayStripeHPP from '../../pay-stripe-hpp.vue';
import PaySuccTip from '../../pay-succ-tip.vue';
import PaySucc from '../../pay-succ.vue';
import TitleLabel from '../../titleLabel/index.vue';
import RightsDesc from '../rights-desc.vue';

const { $t } = useNuxtApp();

const props = withDefaults(
  defineProps<{
    inline: boolean; // 默认 false，用于弹窗，true 用于内嵌页面
    rightsType: USER_LEVEL_KEY;
    behavior: ORDER_BEHAVIOR;
    order_type: ORDER_TYPE;
    paySuccessUpdateUser?: boolean; // 支付完成后是否更新用户信息
    headers?: Record<string, string>;
    descClass?: string;
    userInfo?: UserInfo;
    timeIdentity?: string; // 订单的时间标识
    hppConfig?: any; // Stripe的配置方法
    contentClass?: string;
    onSuccessCallback?(): void; // 成功之后的回调，按需传入
    onCloseParent?(): void; // 点击“创建团队”时，需要关闭弹窗（如果是嵌入弹窗的话）
  }>(),
  {
    inline: false,
    onSuccessCallback() {},
  },
);

const userInfo = computed(() => {
  return props.userInfo || useUserInfo().value;
});

const isRenew = computed(() => userInfo.value!.rights_type === USER_LEVEL_KEY.TEAM_LIGHT);

const { rights, getRights, rightsCompare, currentFees } = useRights(props.rightsType, fees => {
  let fee = fees[0];
  if (isGlobalVersion()) {
    const match = fees.find(v => v.buy_type === BUY_TYPE.SUBSCRIBE);
    if (match) {
      fee = match;
    }
  } else {
    const match = fees.find(v => v.buy_type === BUY_TYPE.PAYMENT);
    if (match) {
      fee = match;
    }
  }
  return fee;
});
const { isInviteUser, inviterAward, getInviteInfo } = useCheckUserInvited(currentFees);

const yuanFormat = computed(() => formatPrice(currentFees.price).string);

const { $dayjs } = useTime();

const expiredDateRange = computed(() => {
  const endDate = $dayjs()
    .add(currentFees?.days || 0, 'day')
    .format('YYYY.MM.DD');

  if (isRenew.value && userInfo.value!.personal_expired_time) {
    const beginDate = $dayjs(userInfo.value!.personal_expired_time).format('YYYY.MM.DD');
    return `${beginDate}~${endDate}`;
  }
  return endDate;
});

const data = computed(() =>
  isGlobalVersion()
    ? ({
        rights_type: props.rightsType,
        time_identity: currentFees.time_identity,
        first_name: $t('yong-hu'),
        last_name: userInfo.value!.account_name,
        rights_id: currentFees.id,
        email: userInfo.value!.email_address,
        product_url: PRODUCT_IMAGES(props.rightsType),
        success_url: props.hppConfig.success_url,
        cancel_url: props.hppConfig.cancel_url,
      } as IPostCreateOrderConfig)
    : {
        rights_id: currentFees.id,
        behavior: props.behavior,
        rights_type: props.rightsType,
        order_type: props.order_type,
        time_identity: currentFees.time_identity,
        parent_order_id: userInfo.value!.current_order_id,
      },
);

const {
  agreementChecked,
  payType,
  qrcode,
  rawOrder,
  order,
  paySucc,
  onRePay,
  createOrder,
  creating,
} = useOrder(data, props.paySuccessUpdateUser, props.headers);

const onTransferClick = () => {
  createOrder(true);
};

const paySuccTitle = computed(() =>
  isInviteUser.value ? $t('sheng-ji-cheng-gong') : $t('gou-mai-tao-can-cheng-gong'),
);
const paySuccDesc = computed(
  () =>
    `${rights.value?.rights_name} ${currentFees.time_identity}，${$t(
      'cheng-gong-zhi-fu',
    )} ${yuanFormat.value}`,
);

function goInvite() {
  const router = useRouter();
  router.push({ name: PageKey.PULL_NEW });
  // props.onCancel()
}

function switchAccount() {
  const showAccountType = useAccountType();
  trackEvent(TRACK_EVENT.TOGGLE_ACCOUNT_TOP_CLICK);
  showAccountType.value = 'select';
}

function showCurrentContact() {
  showContactNew(getQrcodeURI('b', '28'));
}

function toCreateTeam() {
  if (props.onCloseParent) {
    props.onCloseParent();
  }

  const router = useRouter();
  router.push({ name: PageKey.TEAM_STARTER_CREATE });
}

// 购买成功后首次
watch(
  () => paySucc.value,
  (cur, before) => {
    if (cur !== before && cur) {
      if (isInviteUser.value) {
        getInviteInfo(true);
      }

      useUpdateUser();
      props.onSuccessCallback();
    }
  },
);

onMounted(() => {
  getRights();
  getInviteInfo();
});
</script>

<style lang="scss">
.rights-badge {
  position: absolute;
  top: -10px;
  width: 139px;
  color: #fff;
  text-align: center;
  font-weight: 500;
  line-height: 40px;
  background: linear-gradient(225deg, #86c5ff 0%, #6188ff 100%);
  border-radius: 12px 0px 12px 0px;
}
.buyInfo {
  display: flex;
  column-gap: 20px;
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 20px;
  &__label {
    width: 56px;
    opacity: 0.6;
  }
  &__text {
    font-weight: 500;
  }
}
</style>
<style lang="scss" scoped>
.rights-tip {
  margin: 18px 24px 0;
  display: flex;
  align-items: flex-start;
  padding: 9px 17px 7px 12px;
  font-size: 12px;
  line-height: 24px;
  background: #fffbe6;
  border-radius: 8px;
  border: 1px solid #fff1b8;
}
</style>
